import {
  Checkbox as CheckboxPrimitive,
  Label as LabelPrimitive,
  Switch as SwitchPrimitive,
} from "radix-ui";
import { createContext, useContext, type ComponentProps } from "react";

import { cn } from "../lib/cn";
import { Icon } from "./Icon";

/**
 * The id of the surrounding `FormField`. Controls rendered through a wrapper (e.g. React Hook Form's `Controller`)
 * don't receive the id `FormField` passes to its direct child, so `Switch`, `Select` and the date pickers read it
 * from here.
 */
export const FieldIdContext = createContext<string | undefined>(undefined);

export function Label({ className, ...props }: ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      className={cn("text-sm font-medium text-foreground peer-disabled:opacity-60", className)}
      {...props}
    />
  );
}

export function Checkbox({ className, ...props }: ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      className={cn(
        "group peer flex size-5 shrink-0 items-center justify-center rounded-md border border-input bg-surface " +
          "data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-brand-foreground " +
          "data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand data-[state=indeterminate]:text-brand-foreground " +
          "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {/* ✓ when checked, a dash when only some of what it controls is selected */}
        <Icon
          name="check"
          size={14}
          strokeWidth={3}
          className="group-data-[state=indeterminate]:hidden"
        />
        <span className="hidden h-0.5 w-2.5 rounded-full bg-current group-data-[state=indeterminate]:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export function Switch({ className, id, ...props }: ComponentProps<typeof SwitchPrimitive.Root>) {
  const fieldId = useContext(FieldIdContext);
  return (
    <SwitchPrimitive.Root
      id={id ?? fieldId}
      className={cn(
        "peer inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors " +
          "data-[state=checked]:bg-brand data-[state=unchecked]:bg-input disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="block size-5 rounded-full bg-surface shadow-card transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0" />
    </SwitchPrimitive.Root>
  );
}
